import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../sp-firebase/firebase';
import { Account, AuthMethod, SSOProviderConfig } from '../sp-types/accountTypes';

export interface AuthConfig {
    accountId: string | null;
    accountName: string | null;
    ssoProvider: SSOProviderConfig | null;
    allowedMethods: AuthMethod[];
    requireSSO: boolean;
}

// Default config for unknown domains - allow all methods by default
const DEFAULT_AUTH_CONFIG: AuthConfig = {
    accountId: null,
    accountName: null,
    ssoProvider: null,
    allowedMethods: ['email', 'google', 'sso'],
    requireSSO: false,
};

// Cache for domain lookups (avoid repeated Firestore queries)
const domainCache: Map<string, { config: AuthConfig; timestamp: number }> = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Extract domain from email address
 */
export function extractDomain(email: string): string | null {
    const match = email.match(/@([^@]+)$/);
    return match ? match[1].toLowerCase() : null;
}

/**
 * Check if email looks valid (has @ and domain with .)
 */
export function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Lookup auth config by email domain
 * Returns cached result if available and not expired
 */
export async function getAuthConfigByEmail(email: string): Promise<AuthConfig> {
    const domain = extractDomain(email);
    if (!domain) {
        return DEFAULT_AUTH_CONFIG;
    }

    // Check cache
    const cached = domainCache.get(domain);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.config;
    }

    try {
        // Query accounts collection for matching domain
        const accountsRef = collection(db, 'accounts');
        const snapshot = await getDocs(accountsRef);

        let matchedAccount: (Account & { id: string }) | null = null;

        // Find account with matching domain
        for (const doc of snapshot.docs) {
            const account = { ...doc.data(), id: doc.id } as Account & { id: string };
            if (account.domains?.some(d => d.toLowerCase() === domain)) {
                matchedAccount = account;
                break;
            }
        }

        let config: AuthConfig;

        if (matchedAccount) {
            config = {
                accountId: matchedAccount.id,
                accountName: matchedAccount.companyDetails?.name || matchedAccount.id,
                ssoProvider: matchedAccount.ssoProvider || null,
                allowedMethods: matchedAccount.requireSSO
                    ? ['sso']
                    : (matchedAccount.allowedAuthMethods || ['email', 'google']),
                requireSSO: matchedAccount.requireSSO || false,
            };
        } else {
            config = DEFAULT_AUTH_CONFIG;
        }

        // Cache the result
        domainCache.set(domain, { config, timestamp: Date.now() });

        return config;
    } catch (error: any) {
        // Handle permission errors gracefully - user not authenticated yet
        // This is expected on the login page before authentication
        if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
            console.log('Auth config lookup requires authentication - using defaults');
        } else {
            console.error('Error looking up auth config:', error);
        }

        // Cache the default to avoid repeated failed lookups
        domainCache.set(domain, { config: DEFAULT_AUTH_CONFIG, timestamp: Date.now() });
        return DEFAULT_AUTH_CONFIG;
    }
}

/**
 * Check if a specific auth method is allowed for the given config
 */
export function isAuthMethodAllowed(config: AuthConfig, method: AuthMethod): boolean {
    if (config.requireSSO) {
        return method === 'sso';
    }
    return config.allowedMethods.includes(method);
}

/**
 * Clear the domain cache (useful for testing or after admin changes)
 */
export function clearAuthConfigCache(): void {
    domainCache.clear();
}
